import type { Cart } from '@iheartjane/data-access';
import { useCart } from '@iheartjane/data-access/react-query';
import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import { useMemo } from 'react';

import { selectCart } from '@jane/monolith/customer/redux/cart';
import { selectEmbeddedApp } from '@jane/monolith/customer/redux/embeddedApp';
import { selectPosCart } from '@jane/monolith/customer/redux/posCart';
import { useCustomerSelector } from '@jane/monolith/customer/selectors';
import { getJwt } from '@jane/shared/auth';
import { useEcommApp } from '@jane/shared/providers';

import { useLocalCartUuid } from './localCartUuid';
import { mapV1CartToV3 } from './mapV1CartToV3';
import type { V1CartInput } from './mapV1CartToV3';

/**
 * The read side of the cart facade, in the canonical v3 `Cart` shape.
 *
 * Behind `c3UseV3Cart` the cart comes from `GET /v3/carts/:uuid`, keyed by a uuid
 * from localStorage (see {@link useLocalCartUuid}); flag-off it comes from the v1
 * Redux slice through `mapV1CartToV3`. The returned shape is identical either way,
 * which is what makes the flip a body swap rather than a change at 20 call sites.
 *
 * Severance is deliberately partial: `isMutating` / `isApplyingLoyalty` stay on
 * the Redux slice on both paths, because every cart *write* is still a v1 thunk.
 *
 * Which is why the flag must not be enabled until the writes flip with it: no v1
 * thunk invalidates `['cart', uuid]`, so v3 reads over v1 writes serve a stale cart.
 * TODO(carts-v3): remove the flag-off path once the v3 backing is the only source.
 */
export interface CartFacadeState {
  cart: Cart | null;
  /** Total item count (sum of product counts), not the distinct-line count. */
  cartSize: number;
  /**
   * The load has *settled* — not "a cart arrived". Flag-on this cannot be React
   * Query's `isFetched` alone: with no uuid the query is disabled and `isFetched`
   * stays `false` forever, hanging every consumer's initial-load gate on an empty
   * bag.
   */
  hasLoaded: boolean;
  hasSpecial: boolean;
  /**
   * True while a loyalty redemption started by `useCartActions().applyLoyalty`
   * is in flight. Deliberately narrower than `isMutating` (which folds in
   * checkout and four other flags): this backs the double-apply guard on the
   * reward buttons, so widening it would block a legitimate apply during an
   * unrelated cart mutation. Exposed on the facade — rather than left as a
   * direct `selectCart` read at each call site — so the guard and the write it
   * guards move together when `applyLoyalty` is repointed at the v3 mutation.
   */
  isApplyingLoyalty: boolean;
  isFetching: boolean;
  /** An initial load only, unlike `isFetching`. */
  isLoading: boolean;
  /**
   * True while a *trackable* cart mutation is in flight — add-to-cart, checkout,
   * loyalty-apply, reservation-mode change, or unavailable-product deletion.
   * Composed here because the v1 slice has no single mutation flag, so pending UI
   * can be added in one place rather than retrofitted per call site. This does
   * NOT cover every facade action: `updateProduct`, `removeProduct`,
   * `updateDeliveryAddress`, and `removeLoyalty` dispatch thunks that set no
   * in-flight flag on the v1 slice, so they are invisible here (see the
   * composition comment for the full list).
   *
   * Redux-backed on *both* flag paths: every cart write is still a v1 thunk, so
   * flag-on there is no other source. The v3 mutations' `isPending` takes over
   * when the write path flips.
   */
  isMutating: boolean;
  /** Canonical order total consumers display. */
  orderTotal: number;
}

export const useCartState = (): CartFacadeState => {
  const useV3Cart = useFlag(FLAGS.c3UseV3Cart);
  const { appMode } = useEcommApp();
  // Only feeds the legacy-cart adoption read inside `useLocalCartUuid`, which is
  // also the only reason this slice is read here
  const { partnerId: storeId } = useCustomerSelector(selectEmbeddedApp);
  // Gated because the value is unused flag-off, and the read is not free
  const localCartUuid = useLocalCartUuid(appMode, storeId, !!useV3Cart);

  const {
    cart: v1Cart,
    isLoading,
    hasLoaded,
    isAddingItemToCart,
    isApplyingCrmRedemption,
    isChangingReservationModes,
    isCheckingOut,
    isDeletingUnavailableProducts,
    isFixingUnavailableCheckout,
  } = useCustomerSelector(selectCart);

  // `FetchCartProps` also accepts `deliveryAddress` / `paymentMethod`, deliberately
  // not passed: both are still Redux checkout draft, `cartKeys.detail` does not key
  // on the address (so a change would serve a stale cart), and omitting the payment
  // method keeps the key at the `['cart', uuid]` the v3 mutations write to. Delivery
  // fee and tax therefore compute without the address here until 015c.
  const v3Cart = useCart(
    { appMode, jwt: useV3Cart ? getJwt() : null, uuid: localCartUuid ?? '' },
    { enabled: !!useV3Cart }
  );

  // POS totals are on their own slice, not the v1 cart, so consumers that branch on
  // `pos_cart.totals` take the same tax/total branch as the pre-facade path. Read
  // here rather than in the mapper, which stays Redux-free. The v3 payload carries
  // `pos_cart` itself, so this feeds flag-off only.
  const posCartTotals =
    useCustomerSelector(selectPosCart).posCart.totals ?? null;

  // An absent cart has an empty uuid (the slice's default). The cast crosses two
  // axes, both safe: the slice stores the cart `DeepReadonly` and the pure mapper
  // never mutates it, and the slice's runtime cart is the `@jane/shared/models`
  // shape, which `V1CartInput` models directly (widened ids + `amounts_manager`)
  // rather than papering over.
  const mappedV1Cart = useMemo(
    () =>
      !useV3Cart && v1Cart.uuid
        ? mapV1CartToV3(v1Cart as unknown as V1CartInput, posCartTotals)
        : null,
    [useV3Cart, v1Cart, posCartTotals]
  );

  const cart = useV3Cart ? (v3Cart.data ?? null) : mappedV1Cart;

  const cartSize = cart
    ? cart.products.reduce((total, product) => total + product.count, 0)
    : 0;

  const hasSpecial = Boolean(
    cart &&
    (cart.cart_discount_special_id != null ||
      cart.group_special_id != null ||
      cart.brand_discounts.length > 0 ||
      cart.products.some((product) => product.special_id != null))
  );

  return {
    cart,
    // With no uuid the query never runs, so "settled" is immediate.
    hasLoaded: useV3Cart ? !localCartUuid || v3Cart.isFetched : hasLoaded,
    isLoading: useV3Cart ? v3Cart.isLoading : isLoading && !hasLoaded,
    isFetching: useV3Cart ? v3Cart.isFetching : isLoading,
    // Every trackable in-flight flag that backs a facade action. Several facade
    // mutations set NO in-flight flag on the v1 slice and so cannot be reflected
    // here: `updateProduct` (`updateCart`) and `removeProduct` (`deleteCartItem`)
    // dispatch only their success actions; `removeLoyalty` (`removeCrmRedemption`)
    // toggles nothing; and `updateDeliveryAddress` (`initiateUpdateDeliveryAddress`)
    // sets neither a flag nor `isLoading`, so it is invisible to `isFetching` too.
    // These are genuinely untrackable on the Redux path — do not invent flags.
    isMutating:
      isAddingItemToCart ||
      isApplyingCrmRedemption ||
      isChangingReservationModes ||
      isCheckingOut ||
      isDeletingUnavailableProducts ||
      isFixingUnavailableCheckout,
    isApplyingLoyalty: isApplyingCrmRedemption,
    cartSize,
    hasSpecial,
    orderTotal: cart?.order_total ?? 0,
  };
};
